import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { Activity, Zap, AlertTriangle, PhoneOff, Webhook } from 'lucide-react';

const SystemHealthGauges = ({ reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const animationDuration = reducedMotion ? 0 : 0.6;

  useEffect(() => {
    fetchHealthData();
    const interval = setInterval(fetchHealthData, 15000); // Refresh every 15s
    return () => clearInterval(interval);
  }, []);

  const fetchHealthData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/ops/health`);
      const result = await response.json();
      setHealthData(result);
    } catch (error) {
      console.error('Error fetching health data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGaugeColor = (value, metric) => {
    const thresholds = {
      api_latency_ms: { green: 200, amber: 500 },
      llm_latency_ms: { green: 1000, amber: 2000 },
      error_rate: { green: 0.05, amber: 0.15 },
      dropped_calls_1h: { green: 5, amber: 20 },
      webhook_failures_5m: { green: 2, amber: 5 }
    };

    const t = thresholds[metric];
    if (!t) return '#10b981';

    if (value <= t.green) return '#10b981'; // Green
    if (value <= t.amber) return '#f59e0b'; // Amber
    return '#ef4444'; // Red
  };

  const getGaugePercent = (value, metric) => {
    const maxValues = {
      api_latency_ms: 1000,
      llm_latency_ms: 3000,
      error_rate: 0.5,
      dropped_calls_1h: 50,
      webhook_failures_5m: 10
    };

    const max = maxValues[metric] || 100;
    return Math.min((value / max) * 100, 100);
  };

  const Gauge = ({ title, value, metric, icon: Icon }) => {
    const percent = getGaugePercent(value, metric);
    const color = getGaugeColor(value, metric);
    const rotation = (percent / 100) * 180 - 90;

    return (
      <motion.div
        className="relative"
        initial={reducedMotion ? {} : { opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: animationDuration }}
      >
        <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700 hover:border-gold/400 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <Icon className="w-5 h-5" style={{ color }} />
            <span className="text-xs text-slate-400">{title}</span>
          </div>

          {/* Gauge SVG */}
          <div className="relative w-32 h-32 mx-auto mb-4">
            <svg viewBox="0 0 100 100" className="transform rotate-180">
              {/* Background arc */}
              <path
                d="M 15 50 A 35 35 0 0 1 85 50"
                fill="none"
                stroke="#334155"
                strokeWidth="8"
                strokeLinecap="round"
              />
              {/* Value arc with sweep animation */}
              <motion.path
                d="M 15 50 A 35 35 0 0 1 85 50"
                fill="none"
                stroke={color}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray="110"
                initial={{ strokeDashoffset: 110 }}
                animate={{ strokeDashoffset: 110 - (110 * percent / 100) }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </svg>

            {/* Center value */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <motion.span
                className="text-2xl font-bold"
                style={{ color }}
                key={value}
                initial={reducedMotion ? {} : { scale: 1.2, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {typeof value === 'number' && value < 1 ? `${(value * 100).toFixed(1)}%` : Math.round(value)}
              </motion.span>
              <span className="text-xs text-slate-400 mt-1">
                {metric.includes('latency') ? 'ms' : metric.includes('rate') ? '' : ''}
              </span>
            </div>
          </div>

          {/* Status indicator */}
          <div className="flex items-center justify-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }}></div>
            <span className="text-xs text-slate-400">
              {percent <= 33 ? 'Healthy' : percent <= 66 ? 'Warning' : 'Critical'}
            </span>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <Activity className="w-5 h-5 text-emerald-400" />
          System Health Gauges
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Real-time operational health • Refresh every 15s
        </p>
      </CardHeader>
      <CardContent>
        {loading && !healthData ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-slate-400">Loading health metrics...</div>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Gauge
                title="API Latency"
                value={healthData?.api_latency_ms || 0}
                metric="api_latency_ms"
                icon={Zap}
              />
              <Gauge
                title="LLM Latency"
                value={healthData?.llm_latency_ms || 0}
                metric="llm_latency_ms"
                icon={Zap}
              />
              <Gauge
                title="Error Rate"
                value={healthData?.error_rate || 0}
                metric="error_rate"
                icon={AlertTriangle}
              />
              <Gauge
                title="Dropped (1h)"
                value={healthData?.dropped_calls_1h || 0}
                metric="dropped_calls_1h"
                icon={PhoneOff}
              />
              <Gauge
                title="Webhook Fails"
                value={healthData?.webhook_failures_5m || 0}
                metric="webhook_failures_5m"
                icon={Webhook}
              />
            </div>

            {healthData && (
              <div className="mt-6 text-center">
                <p className="text-xs text-slate-500">
                  Sample: {healthData.sample_size} calls • Updated: {new Date(healthData.updated_at).toLocaleTimeString()}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SystemHealthGauges;
